<script setup>
import {ref, computed} from 'vue';

// Import emoji picker only on client-side to avoid SSR issues with IndexedDB
let EmojiPicker = null;
if (process.client) {
  EmojiPicker = (await import('vue3-emoji-picker')).default;
  await import('vue3-emoji-picker/css');
}

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['send', 'send-image', 'typing']);

const messageText = ref('');
const sending = ref(false);
const typingTimeout = ref(null);
const isTyping = ref(false);
const showEmojiPicker = ref(false);
const textareaRef = ref(null);

// Image handling
const fileInputRef = ref(null);
const selectedImageFile = ref(null);
const selectedImageUrl = ref(null);
const showCropper = ref(false);
const croppedImageBlob = ref(null);
const imagePreviewUrl = ref(null);

const canSend = computed(() => {
  const hasText = messageText.value?.trim().length > 0;
  const hasImage = croppedImageBlob.value !== null;
  return (hasText || hasImage) && !sending.value && !props.disabled;
});

const handleSend = async () => {
  if (!canSend.value) return;

  sending.value = true;

  // Stop typing indicator
  handleStopTyping();

  try {
    if (croppedImageBlob.value) {
      // Send image message
      const caption = messageText.value.trim();
      await emit('send-image', croppedImageBlob.value, caption);

      // Clear image state
      removeImage();
    } else {
      // Send text message
      const text = messageText.value.trim();
      await emit('send', text);
    }

    messageText.value = '';
  } catch (error) {
    console.error('Error sending message:', error);
  } finally {
    sending.value = false;
  }
};

const handleTyping = () => {
  // Send typing indicator if not already typing
  if (!isTyping.value) {
    isTyping.value = true;
    emit('typing', true);
  }

  // Clear previous timeout
  if (typingTimeout.value) {
    clearTimeout(typingTimeout.value);
  }

  // Set new timeout to stop typing after 2 seconds of inactivity
  typingTimeout.value = setTimeout(() => {
    handleStopTyping();
  }, 2000);
};

const handleStopTyping = () => {
  if (isTyping.value) {
    isTyping.value = false;
    emit('typing', false);
  }

  if (typingTimeout.value) {
    clearTimeout(typingTimeout.value);
    typingTimeout.value = null;
  }
};

const onEmojiSelect = (emoji) => {
  messageText.value += emoji.i;
  showEmojiPicker.value = false;

  // Trigger typing indicator
  handleTyping();
};

// Image handling methods
const handleImageSelect = (event) => {
  const file = event.target.files?.[0];
  if (!file) return;

  // Validate file type
  if (!file.type.startsWith('image/')) {
    alert('Please select an image file');
    return;
  }

  // Validate file size (max 10MB)
  if (file.size > 10 * 1024 * 1024) {
    alert('Image size must be less than 10MB');
    return;
  }

  selectedImageFile.value = file;
  showCropper.value = true;
};

const handleCroppedImage = async (base64Data) => {
  // Convert base64 to Blob
  const response = await fetch(base64Data);
  const blob = await response.blob();

  // Extract MIME type from base64 data URL (e.g., "data:image/png;base64,...")
  const mimeTypeMatch = base64Data.match(/data:([^;]+);base64,/);
  const mimeType = mimeTypeMatch ? mimeTypeMatch[1] : 'image/jpeg';

  // Determine file extension from MIME type
  const extension = mimeType.split('/')[1] || 'jpg';

  // Use original filename with correct extension
  const originalName = selectedImageFile.value?.name || 'image.jpg';
  const nameWithoutExt = originalName.replace(/\.[^.]+$/, '');
  const fileName = `${nameWithoutExt}.${extension}`;

  const file = new File([blob], fileName, {type: mimeType});

  croppedImageBlob.value = file;
  imagePreviewUrl.value = base64Data;
  showCropper.value = false;

  // Clean up
  selectedImageFile.value = null;

  // Reset file input
  if (fileInputRef.value) {
    fileInputRef.value.value = '';
  }
};

const handleCropperCancel = () => {
  showCropper.value = false;

  // Clean up
  selectedImageFile.value = null;

  // Reset file input
  if (fileInputRef.value) {
    fileInputRef.value.value = '';
  }
};

const removeImage = () => {
  if (imagePreviewUrl.value && imagePreviewUrl.value.startsWith('blob:')) {
    URL.revokeObjectURL(imagePreviewUrl.value);
  }
  croppedImageBlob.value = null;
  imagePreviewUrl.value = null;
};

const openImagePicker = () => {
  if (fileInputRef.value) {
    fileInputRef.value.click();
  }
};
</script>

<template>
  <div class="composer-wrap">
    <!-- Image preview -->
    <div v-if="imagePreviewUrl" class="composer-preview">
      <v-img :src="imagePreviewUrl" width="56" height="56" class="rounded" cover />
      <div class="composer-preview-meta">
        <div>Image ready to send</div>
        <div class="composer-preview-name">{{ croppedImageBlob?.name || 'image.jpg' }}</div>
      </div>
      <button type="button" class="composer-preview-x" aria-label="Remove image" @click="removeImage">
        <v-icon size="16">mdi-close</v-icon>
      </button>
    </div>

    <!-- Hidden file input -->
    <input ref="fileInputRef" type="file" accept="image/*" style="display: none" @change="handleImageSelect" />

    <!-- Composer (mockup .composer) -->
    <div class="composer">
      <button type="button" class="composer-ic" :disabled="disabled" aria-label="Attach an image" @click="openImagePicker">
        <v-icon size="20">mdi-image-outline</v-icon>
      </button>

      <v-menu v-model="showEmojiPicker" :close-on-content-click="false" location="top" offset="8">
        <template #activator="{ props: menuProps }">
          <button type="button" class="composer-ic d-none d-md-flex" v-bind="menuProps" :disabled="disabled" aria-label="Add an emoji">
            <v-icon size="20">mdi-emoticon-outline</v-icon>
          </button>
        </template>
        <v-card class="emoji-picker-card">
          <client-only>
            <emoji-picker v-if="EmojiPicker" :native="true" @select="onEmojiSelect" />
          </client-only>
        </v-card>
      </v-menu>

      <div class="composer-input">
        <v-textarea
            ref="textareaRef"
            v-model="messageText"
            :placeholder="imagePreviewUrl ? 'Add a caption (optional)…' : 'Message…'"
            rows="1"
            auto-grow
            max-rows="4"
            variant="plain"
            density="compact"
            hide-details
            @keydown.enter.exact.prevent="handleSend"
            @input="handleTyping"
            @blur="handleStopTyping"
            :disabled="disabled"
        />
      </div>

      <button type="button" class="composer-send" :disabled="!canSend" aria-label="Send" @click="handleSend">
        <v-progress-circular v-if="sending" indeterminate size="18" width="2" color="white" />
        <v-icon v-else size="20">mdi-send</v-icon>
      </button>
    </div>

    <!-- Image Cropper Dialog -->
    <crud-image-cropper
        :aspect-ratio="null"
        v-if="showCropper && selectedImageFile"
        :image-file="selectedImageFile"
        @crop="handleCroppedImage"
        @cancel="handleCropperCancel"
        :hide-aspect-ratio-selector="true"
    />
  </div>
</template>

<style scoped lang="scss">
.composer-wrap {
  background: rgb(var(--v-theme-surface));
}

/* image preview strip */
.composer-preview {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-top: 1px solid rgb(var(--v-theme-borderLight));
}

.composer-preview-meta {
  flex: 1;
  min-width: 0;
  font-size: 12px;
  color: rgb(var(--v-theme-textSubtle));
}

.composer-preview-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.composer-preview-x {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: rgb(var(--v-theme-primary));
  background: rgb(var(--v-theme-brandTint));
  flex-shrink: 0;
}

/* composer row */
.composer {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-top: 1px solid rgb(var(--v-theme-borderLight));
}

.composer-ic {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: rgb(var(--v-theme-textSubtle));
  flex-shrink: 0;

  &:hover {
    background: rgb(var(--v-theme-light));
  }

  &:disabled {
    opacity: 0.45;
    pointer-events: none;
  }
}

/* pill input wrapping the (borderless) textarea */
.composer-input {
  flex: 1;
  min-width: 0;
  background: rgb(var(--v-theme-bg, 250, 250, 250));
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 22px;
  padding: 2px 14px;
  display: flex;
  align-items: center;
}

.composer-input :deep(.v-field) {
  padding: 0;
}

.composer-input :deep(.v-field__input) {
  padding: 8px 0;
  min-height: 24px;
  font-size: 13px;
  mask-image: none;
  -webkit-mask-image: none;
}

.composer-send {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: rgb(var(--v-theme-primary));
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:disabled {
    opacity: 0.45;
    pointer-events: none;
  }
}

.emoji-picker-card {
  max-width: 350px;
  overflow: hidden;
}
</style>
